/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import './NewUserForm.scss';
import '../NewUserFlow.scss';

import { func, object, string, bool } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import ProfileStackedLabeledInput from 'components/layouts/Preferences/shared/ProfileStackedLabeledInput';
import FormattedInput from 'components/shared/FormattedInput/FormattedInput';
import { isValidTaxId, isValidFirstLastName } from 'services/Validation';
import ActionButton from 'components/shared/Buttons/ActionButton';
import LoadingButton from 'components/shared/LoadingButton/LoadingButton';
import TRP from 'components/shared/TRP/TRP';

/**
 * Function to handle a scroll event
 * @name ScrollBottomHandler
 * @function
 * @param {Event} scrollEvent
 */
// TODO: is there a utility file this could go in
/**
 * An event handler that fires when a scrollable element has been scrolled to the bottom.
 * @param {ScrollBottomHandler} scrollBottomHandler
 */
const handleScrollToBottom = (scrollBottomHandler) => (scrollEvent) => {
  // Taken from https://stackoverflow.com/questions/45585542/detecting-when-user-scrolls-to-bottom-of-div-with-react-js/45586395
  const { scrollHeight, scrollTop, clientHeight } = scrollEvent.currentTarget;
  const bottom = scrollHeight - scrollTop < clientHeight + clientHeight * 0.1; // `clientHeight * 0.1` creates an acceptable distance margin to loosen constraints

  if (bottom) {
    scrollBottomHandler(scrollEvent);
  }
};

const NewUserForm = ({
  errorMsgText,
  firstName,
  invalidFields,
  lastName,
  onClose = () => {},
  onContinue = () => {},
  shouldDisableNext,
  ssn,
  touchedFields,
  updateFirstName,
  updateLastName,
  updateSSN,
  updateScrollDetection
}) => {
  const nameValidator = (field) => ({
    field,
    shouldValidate: touchedFields[field] === true,
    validator: isValidFirstLastName,
    value: { firstName, lastName }[field]
  });

  const taxIdValidator = {
    field: 'ssn',
    shouldValidate: touchedFields.ssn === true,
    validator: isValidTaxId,
    value: ssn
  };

  return (
    <Fragment>
      <div
        className="add-new-user-form-wrapper"
        data-testid="scrollable-modal-form"
        onScroll={handleScrollToBottom(() => updateScrollDetection(true))}>
        <div className="add-new-user-form">
          <ProfileStackedLabeledInput
            errorMessage={invalidFields['firstName']}
            label="First Name"
            validators={[nameValidator('firstName')]}>
            <input
              className="address-input"
              onChange={updateFirstName}
              placeholder="Enter First Name"
              value={firstName}
            />
          </ProfileStackedLabeledInput>
          <ProfileStackedLabeledInput
            errorMessage={invalidFields['lastName']}
            label="Last Name"
            validators={[nameValidator('lastName')]}>
            <input
              className="address-input"
              onChange={updateLastName}
              placeholder="Enter Last Name"
              value={lastName}
            />
          </ProfileStackedLabeledInput>
          <ProfileStackedLabeledInput
            errorMessage={invalidFields['taxId']}
            label="Social Security Number"
            validators={[taxIdValidator]}>
            <FormattedInput
              className="address-input blocked_data"
              format={'ssn'}
              onChange={updateSSN}
              placeholder="XXX-XX-XXXX"
              value={ssn}
            />
          </ProfileStackedLabeledInput>
        </div>
        <p className="new-user-requirement-copy">
          The above must match an existing on-line enabled individual&apos;s
          information exactly. <strong>All fields required.</strong>
        </p>
        <div className="new-user-disclosures">
          <strong>Customer Agreement</strong>
          <p>
            I hereby authorize the above individual to view information
            concerning the account(s) that I have designated through my use of
            this online tool, subject to the terms and conditions listed below.
          </p>

          <strong>View Access Authorization</strong>
          <p>
            By electing to grant access to view the account(s) you have
            designated, you, on behalf of yourself and any other account owners
            of the account(s) for which view access has been authorized
            (&quot;Account(s)&quot;), are granting the individual you have
            designated (&quot;Authorized Person&quot;) with the ability to
            access information online concerning the Account(s). You authorize
            the <TRP /> entity providing services for such Account(s), such as{' '}
            <TRP /> Services, Inc., <TRP /> Investment Services, Inc., or{' '}
            <TRP /> Advisory Services, Inc., and its affiliates (collectively,
            &quot;
            <TRP />
            &quot;) to provide to the Authorized Person any information
            available to an account owner online, including, but not limited to,
            balances, pending transactions, transaction history, and performance
            information for the Account(s).* Your authorization is a continuing
            one, and shall remain in full force and effect until you take action
            to revoke it and <TRP /> has had a reasonable opportunity to act on
            your revocation. You may revoke your authorization through your use
            of this online tool to remove the Authorized Person(s) or by written
            notice of revocation delivered to <TRP />. This authorization does
            not permit Authorized Person(s) to purchase, exchange, or redeem
            shares from the Account(s).
          </p>

          <strong>Indemnification</strong>
          <p>
            <TRP /> shall assume no responsibility for the actions of such
            Authorized Person(s), including any unauthorized or fraudulent use
            of the Account information. On behalf of yourself and any other
            owners of the Account(s), you agree to indemnify and hold <TRP />,
            its agents, any 529 plans for whom <TRP /> acts as program manager,
            and the <TRP /> Funds harmless from any and all losses (including
            reasonable attorneys’ fees) that may arise from the acts or
            omissions of the Authorized Person(s) with respect to the Account(s)
            or any other account maintained by you or the other account owners
            at <TRP />. By clicking below, you agree to these provisions.
          </p>
          <p>
            *Tax information is not currently available but may be available in
            the future.
          </p>
        </div>
      </div>

      <div className="new-user-modal-button-group">
        <LoadingButton
          className="button new-user-modal-next"
          disabled={shouldDisableNext}
          id="submit-form-button"
          onClick={onContinue}
          requestStatusKey="grantViewAccessAddUserAuth"
          type="primary">
          Next
        </LoadingButton>
        <ActionButton
          className="button cancel-button"
          id="cancel-new-user"
          onClick={onClose}
          type="secondary">
          Cancel
        </ActionButton>
      </div>
      {errorMsgText && (
        <div className="gva-authorization-error">{errorMsgText}</div>
      )}
    </Fragment>
  );
};

NewUserForm.propTypes = {
  errorMsgText: string,
  firstName: string,
  invalidFields: object,
  lastName: string,
  onClose: func,
  onContinue: func,
  shouldDisableNext: bool,
  ssn: string,
  touchedFields: object,
  updateFirstName: func,
  updateLastName: func,
  updateSSN: func
};

const mapStateToProps = (state) => ({
  invalidFields: state.invalidFields,
  touchedFields: state.touchedFields
});

export default connect(mapStateToProps)(NewUserForm);
